import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Check, Circle, Mic, Trophy } from 'lucide-react';
import {
  listenToStandings, listenToTracks, useCoverage, useMyTrackIds,
  type Standing, type Track,
} from '@core/firebase/tracks';
import { surahProgress, trackProgress } from '@core/quran/trackProgress';
import { firstUncoveredAyah } from '@core/quran/curriculumProgress';
import { surahMeta } from '@core/quran/surahMeta';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { ProgressBar } from '@/components/ProgressBar';
import { LoadingBlock, PageHeader, PILL_PRIMARY } from '@/components/learning/ui';
import { useLearnText } from '@/lib/learnText';
import { cn } from '@/lib/cn';

/**
 * One track: what it contains, how far the reader has come, and who else is in
 * it.
 *
 * The board is the ONE thing anyone learns about another student — a name and
 * a distance. Their mistakes are their own, and the rules refuse them to a
 * peer, so nothing here can show them.
 *
 * Redesign 2026-10 (board l8), at desktop width: the progress header with its
 * count and the one action — «تابع: سورة …» into practice at the next surah —
 * then the surahs as done / next / later cards, with the board beside them.
 */
export default function TrackPage() {
  const { trackId } = useParams();
  const { identity } = useAuth();
  const { t, language } = useLanguage();
  const tl = useLearnText();
  const [tracks, setTracks] = useState<Track[]>([]);
  const [standings, setStandings] = useState<Standing[]>([]);
  const { trackIds } = useMyTrackIds(identity?.userId ?? null);
  const coverage = useCoverage(identity?.userId ?? null);

  useEffect(() => listenToTracks(setTracks), []);
  useEffect(() => {
    if (!trackId) return undefined;
    return listenToStandings(trackId, setStandings);
  }, [trackId]);

  const track = tracks.find((x) => x.id === trackId);
  const count = (n: number) => new Intl.NumberFormat('en-US').format(n);
  const surahName = (n: number) => {
    const meta = surahMeta(n);
    if (!meta) return String(n);
    return language === 'ar' ? meta.name : meta.englishName;
  };

  if (!track) {
    return <LoadingBlock testId="track-loading" />;
  }

  const joined = trackIds.includes(track.id);
  const mine = trackProgress(coverage, track.surahs);
  const rows = track.surahs.map((surah) => ({ surah, one: surahProgress(coverage, surah) }));
  const doneCount = rows.filter((r) => r.one.total > 0 && r.one.covered >= r.one.total).length;
  // The next surah is the first unfinished one in the track's own order.
  const next = rows.find((r) => r.one.covered < r.one.total) ?? null;

  return (
    <div className="mx-auto w-full max-w-5xl space-y-6">
      <PageHeader
        titleTestId="track-name"
        title={track.name}
        subtitle={(
          <>
            {t('tracks.surahCount', { count: track.surahs.length, n: count(track.surahs.length) })}
            {' · '}
            {t('tracks.ayahCount', { count: track.totalAyahs, n: count(track.totalAyahs) })}
          </>
        )}
      />

      <div className="grid gap-5 lg:grid-cols-[minmax(0,1fr)_320px]">
        <div className="space-y-5">
          <section className="card-soft space-y-4 p-6">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <h2 className="text-lg font-semibold text-ink">{t('tracks.yourProgress')}</h2>
                <p className="text-sm text-helper">
                  {tl('learn.track.doneOf', { done: count(doneCount), total: count(track.surahs.length) })}
                </p>
              </div>
              {joined
                ? <span data-testid="my-percent" className="text-3xl font-semibold text-ink" dir="ltr">{count(mine.percent)}%</span>
                : <span data-testid="not-joined" className="text-sm text-helper">{t('tracks.notJoined')}</span>}
            </div>
            {joined && <ProgressBar percent={mine.percent} label={t('tracks.yourProgress')} />}
            {next ? (
              <Link
                to={`/practice?surah=${next.surah}&ayah=${firstUncoveredAyah(coverage, { surah: next.surah })}`}
                data-testid="track-continue"
                className={cn(PILL_PRIMARY, 'w-full sm:w-auto')}
              >
                <Mic className="h-4 w-4" aria-hidden />
                {tl('learn.track.continueAt', {
                  // The string already says «سورة»; the stored name carries it too.
                  name: surahName(next.surah).replace(/^سُورَةُ\s+/, ''),
                })}
              </Link>
            ) : null}
          </section>

          <ul className="grid gap-3 sm:grid-cols-2">
            {rows.map(({ surah, one }) => {
              const done = one.total > 0 && one.covered >= one.total;
              const isNext = next?.surah === surah;
              return (
                <li
                  key={surah}
                  data-testid={`surah-${surah}`}
                  className={cn('card-soft space-y-3 p-4', isNext && 'bg-tint')}
                >
                  <div className="flex items-center gap-3">
                    <span
                      className={cn(
                        'flex h-9 w-9 shrink-0 items-center justify-center rounded-full',
                        done && 'bg-lime text-accent-foreground',
                        isNext && 'bg-primary text-primary-foreground ring-2 ring-lime ring-offset-2 ring-offset-tint',
                        !done && !isNext && 'bg-surface text-helper',
                      )}
                    >
                      {done ? <Check className="h-4 w-4" aria-hidden />
                        : isNext ? <Mic className="h-4 w-4" aria-hidden />
                          : <Circle className="h-3 w-3" aria-hidden />}
                    </span>
                    <span className={cn('min-w-0 flex-1 truncate font-semibold', done || isNext ? 'text-ink' : 'text-helper')}>
                      {surahName(surah)}
                    </span>
                    <span className="shrink-0 text-xs text-helper">
                      {done ? tl('learn.track.done') : isNext ? tl('learn.track.next') : null}
                    </span>
                  </div>
                  <ProgressBar percent={one.percent} label={surahName(surah)} />
                  <p className="text-xs text-helper"><span dir="ltr">{count(one.covered)} / {count(one.total)}</span></p>
                </li>
              );
            })}
          </ul>
        </div>

        <section className="card-soft h-fit space-y-3 p-5 lg:sticky lg:top-6">
          <h2 className="flex items-center gap-2 font-semibold text-ink">
            <span className="flex h-8 w-8 items-center justify-center rounded-full bg-tint text-ink">
              <Trophy className="h-4 w-4" aria-hidden />
            </span>
            {t('tracks.board')}
          </h2>

          {standings.length === 0 ? (
            <p data-testid="no-members" className="p-4 text-center text-sm text-helper">
              {t('tracks.noMembers')}
            </p>
          ) : (
            <ol className="space-y-1">
              {standings.map((s, i) => {
                const isMe = s.studentId === identity?.userId;
                return (
                  <li
                    key={s.studentId}
                    data-testid={`standing-${s.studentId}`}
                    className={cn(
                      'flex items-center gap-3 rounded-[14px] px-3 py-2 text-sm',
                      isMe && 'bg-tint font-semibold',
                    )}
                  >
                    <span className="w-6 shrink-0 text-helper">{count(i + 1)}</span>
                    <span className="min-w-0 flex-1 truncate text-ink">
                      {s.displayName}
                      {isMe && (
                        <span className="ms-2 rounded-full bg-lime px-2 py-0.5 text-xs text-accent-foreground">
                          {t('tracks.you')}
                        </span>
                      )}
                    </span>
                    <span className="shrink-0 text-helper" dir="ltr">{count(s.percent)}%</span>
                  </li>
                );
              })}
            </ol>
          )}
        </section>
      </div>
    </div>
  );
}
